'use client';

import { useMemo } from 'react';
import { Box, token } from '@terra/ui-styled-system';
import { BodyDefault } from '@terra/ui-design-system';
import {
  Alert01Icon,
  BotIcon,
  CpuIcon,
  PlayIcon,
  Target03Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useAppTargetId } from '../../../store/projectStore';
import { TARGET_TYPE } from '../../../utils/target-scope.utils';
import { useAiApplicationKpiMetrics } from '../../../hooks/dashboard/useAiApplicationKpiMetrics';
import { useHomeVisitBaseline } from '../../../hooks/dashboard/useHomeVisitBaseline';
import { useNeedsAttention } from '../../../hooks/dashboard/useNeedsAttention';
import { usePersistTargetVisit } from '../../../hooks/usePersistTargetVisit';
import { ActivityTrendCard } from '../../dashboard/ActivityTrendCard';
import { HomePageLayout } from '../../dashboard/HomePageLayout';
import { HomeScanningStatusPill } from '../../dashboard/HomeScanningStatusPill';
import { HomeWorkGrid } from '../../dashboard/HomeWorkGrid';
import { PostureStrip } from '../../dashboard/PostureStrip';
import {
  SnapshotSinceLastVisitStrip,
  type SnapshotStripCell,
} from '../../dashboard/SnapshotSinceLastVisitStrip';
import {
  TargetProfileCard,
  type ProfileFactKey,
} from '../../dashboard/TargetProfileCard';
import { TargetReadinessCard } from '../../dashboard/TargetReadinessCard';
import { WelcomeHeader } from '../../dashboard/WelcomeHeader';

const MUTED_ICON_COLOR = token('colors.fg.neutral.muted');

const AI_PROFILE_FACT_KEYS: readonly ProfileFactKey[] = [
  'url',
  'environment',
  'techStack',
  'authMechanisms',
];

export function AiApplicationOverviewTab() {
  const t = useTranslations('home');
  const applicationId = useAppTargetId();
  const targetId = applicationId ?? null;
  const targetType = TARGET_TYPE.AI_APPLICATION;
  const { stats, isLoadingCoreMetrics, isLoadingExecutions } =
    useAiApplicationKpiMetrics({ aiApplicationId: targetId });
  const {
    baselineAt,
    isReady: isBaselineReady,
    previousSnapshot: previousMetrics,
  } = useHomeVisitBaseline({ targetId, targetType });
  const attention = useNeedsAttention({
    applicationId: applicationId ?? '',
    targetType,
  });

  const visitSnapshot = useMemo(() => {
    if (!applicationId || !stats) {
      return null;
    }

    return {
      signalsCount: stats.signals,
      findingsCount: stats.findings,
      executionsCount: stats.executions,
      agentsCount: stats.agents,
      componentsCount: stats.components,
    };
  }, [applicationId, stats]);

  usePersistTargetVisit({ targetId, targetType, snapshot: visitSnapshot });

  if (!applicationId) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <BodyDefault color="fg.neutral.muted">
          {t('selectAiApplication')}
        </BodyDefault>
      </Box>
    );
  }

  const stripCells: SnapshotStripCell[] = [
    {
      key: 'findings',
      label: t('sinceLastVisit.findings'),
      icon: <Alert01Icon size={16} color={MUTED_ICON_COLOR} />,
      current: stats?.findings ?? 0,
      previous: previousMetrics?.findingsCount,
      destination: { kind: 'list', destination: { list: 'findings' } },
    },
    {
      key: 'signals',
      label: t('sinceLastVisit.signals'),
      icon: <Target03Icon size={16} color={MUTED_ICON_COLOR} />,
      current: stats?.signals ?? 0,
      previous: previousMetrics?.signalsCount,
      destination: { kind: 'list', destination: { list: 'signals' } },
    },
    {
      key: 'agents',
      label: t('sinceLastVisit.agents'),
      icon: <BotIcon size={16} color={MUTED_ICON_COLOR} />,
      current: stats?.agents ?? 0,
      previous: previousMetrics?.agentsCount,
      destination: { kind: 'list', destination: { list: 'assets' } },
    },
    {
      key: 'components',
      label: t('sinceLastVisit.components'),
      icon: <CpuIcon size={16} color={MUTED_ICON_COLOR} />,
      current: stats?.components ?? 0,
      previous: previousMetrics?.componentsCount,
      destination: { kind: 'list', destination: { list: 'assets' } },
    },
    {
      key: 'executions',
      label: t('sinceLastVisit.executions'),
      icon: <PlayIcon size={16} color={MUTED_ICON_COLOR} />,
      current: stats?.executions ?? 0,
      previous: previousMetrics?.executionsCount,
      destination: {
        kind: 'list',
        destination: { list: 'executions', filters: { statusFilter: [] } },
      },
    },
  ];

  return (
    <HomePageLayout>
      <WelcomeHeader
        lastVisitAt={isBaselineReady ? baselineAt : undefined}
        status={<HomeScanningStatusPill applicationId={applicationId} />}
      />
      <SnapshotSinceLastVisitStrip
        applicationId={applicationId}
        cells={stripCells}
        isLoading={
          isLoadingCoreMetrics || isLoadingExecutions || !isBaselineReady
        }
      />
      <HomeWorkGrid
        applicationId={applicationId}
        attentionItems={attention.items}
        isAttentionLoading={attention.isLoading}
      />
      <PostureStrip applicationId={applicationId} />
      <TargetReadinessCard applicationId={applicationId} />
      <ActivityTrendCard applicationId={applicationId} />
      <TargetProfileCard
        applicationId={applicationId}
        factKeys={AI_PROFILE_FACT_KEYS}
      />
    </HomePageLayout>
  );
}
